import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Key, Mail, Shield, AlertTriangle, MessageSquare, UserX } from 'lucide-react';
import axios from 'axios';
import DeleteMemoryDialog from './DeleteMemoryDialog';
import DeleteAccountDialog from './DeleteAccountDialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMemoryDialogOpen, setDeleteMemoryDialogOpen] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user'
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/superadmin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!newUser.email || !newUser.password || !newUser.full_name) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      console.log('Creating user with data:', newUser);

      await axios.post(`${API_BASE}/superadmin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User created successfully');
      setCreateDialogOpen(false);
      setNewUser({ email: '', password: '', full_name: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      console.error('Error response:', error.response?.data);

      // Show detailed validation errors if available
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          const errors = detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join('\n');
          toast.error(`Validation error:\n${errors}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Failed to create user');
        }
      } else {
        toast.error('Failed to create user');
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    const userId = selectedUser?.id || selectedUser?._id;
    if (!userId) {
      toast.error('User ID not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE}/superadmin/users/${userId}/password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    const userId = selectedUser?.id || selectedUser?._id;
    if (!userId) {
      toast.error('User ID not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/superadmin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      superadmin: { label: 'SuperAdmin', color: 'bg-purple-100 text-purple-700 border-purple-300' },
      admin: { label: 'Admin', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
      expert: { label: 'Expert', color: 'bg-orange-100 text-orange-700 border-orange-300' },
      user: { label: 'User', color: 'bg-blue-100 text-blue-700 border-blue-300' },
      parent: { label: 'User', color: 'bg-blue-100 text-blue-700 border-blue-300' } // Backward compatibility
    };
    const config = roleConfig[role] || roleConfig.user;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const openDeleteMemoryDialog = (user) => {
    setSelectedUser(user);
    setDeleteMemoryDialogOpen(true);
  };

  const openDeleteAccountDialog = (user) => {
    setSelectedUser(user);
    setDeleteAccountDialogOpen(true);
  };

  const handleDeleteMemory = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/chat/history`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { user_id: selectedUser.id }
      });
      
      toast.success(`Conversation memory deleted for ${selectedUser.full_name}`);
      setDeleteMemoryDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting memory:', error);
      toast.error('Failed to delete conversation memory');
    }
  };

  const handleDeleteAccount = async (password) => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { password, user_id: selectedUser.id }
      });
      
      toast.success(`Account deleted for ${selectedUser.full_name}`);
      setDeleteAccountDialogOpen(false);
      setSelectedUser(null);
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Users className="w-8 h-8 text-emerald-600" />
              User Management
            </h1>
            <p className="text-slate-600 mt-2">
              Manage all users across the platform
            </p>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <UserPlus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Add a new user to the platform with specified role
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser}>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="user@example.com"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      type="text"
                      placeholder="John Doe"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Minimum 6 characters"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="role">Role</Label>
                    <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem key="user" value="user">User (Parent)</SelectItem>
                        <SelectItem key="expert" value="expert">Expert</SelectItem>
                        <SelectItem key="admin" value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                    Create User
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
          <CardDescription>
            {users.length} total users in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user, index) => (
                      <TableRow key={user.id || user._id || `user-${index}`}>
                        <TableCell className="font-medium">{user.full_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            {user.email}
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadge(user.role)}</TableCell>
                        <TableCell className="text-slate-600">
                          {new Date(user.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPasswordDialog(user)}
                              className="hover:bg-blue-50"
                            >
                              <Key className="w-4 h-4 mr-1" />
                              Change Password
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDeleteMemoryDialog(user)}
                              className="hover:bg-orange-50 hover:text-orange-600 hover:border-orange-300"
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Delete Memory
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDeleteAccountDialog(user)}
                              disabled={user.role === 'superadmin'}
                              className="hover:bg-red-50 hover:text-red-600 hover:border-red-300 disabled:opacity-50"
                            >
                              <UserX className="w-4 h-4 mr-1" />
                              Delete Account
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDeleteDialog(user)}
                              disabled={user.role === 'superadmin'}
                              className="hover:bg-red-50 hover:text-red-600 hover:border-red-300 disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Change password for {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Change Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.full_name}</strong>?
              <br />
              <span className="text-red-600 font-semibold mt-2 block">
                This will permanently delete all associated data including children, activities, conversations, and reports.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Memory Dialog */}
      <DeleteMemoryDialog
        isOpen={deleteMemoryDialogOpen}
        onClose={() => setDeleteMemoryDialogOpen(false)}
        onConfirm={handleDeleteMemory}
        user={selectedUser}
      />

      {/* Delete Account Dialog */}
      <DeleteAccountDialog
        isOpen={deleteAccountDialogOpen}
        onClose={() => setDeleteAccountDialogOpen(false)}
        onConfirm={handleDeleteAccount}
        user={selectedUser}
      />
    </div>
  );
};

export default UserManagement;
